import { BookOpen24Regular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PRODUCT_NAME } from '@web/core/app-info';
import { requireSettledSession } from '@web/core/auth/password-change';
import { meQueryOptions } from '@web/core/auth/session';
import { useSessionKeepAlive } from '@web/core/auth/use-session-keep-alive';
import { PermissionsProvider } from '@web/core/permissions/permissions';
import { useBrandColor } from '@web/core/theme/brand';
import { Avatar, AvatarFallback } from '@web/shared/ui/avatar';
import { Badge } from '@web/shared/ui/badge';
import { Button } from '@web/shared/ui/button';

export const Route = createFileRoute('/help')({
  beforeLoad: async ({ context, location }) => {
    await requireSettledSession(context.queryClient, location.href);
  },
  component: HelpLayout,
});

function HelpLayout() {
  const { t } = useTranslation();
  const me = useQuery(meQueryOptions);
  useSessionKeepAlive();
  useBrandColor(me.data?.membership.brandColor);

  if (!me.data) return null;
  const { user, organization, membership } = me.data;

  const initials = user.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <PermissionsProvider permissions={membership.permissions}>
      <div className="flex h-screen w-screen flex-col overflow-hidden bg-background text-foreground">
        {/* Dedicated Help Center Header (clean, without CRM navigation sidebar) */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Link
              to="/help"
              className="flex items-center gap-2 font-semibold text-foreground hover:opacity-90 transition-opacity"
            >
              <div className="flex size-8 items-center justify-center rounded-lg bg-brand-tint text-brand-fg">
                <BookOpen24Regular className="size-4 text-brand" />
              </div>
              <span className="text-base tracking-tight">{organization.name || PRODUCT_NAME}</span>
            </Link>
            <Badge variant="secondary" className="hidden sm:inline-flex text-xs font-normal">
              {t('help.badge', 'Central de Ajuda')}
            </Badge>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              asChild
              className="h-8 gap-1.5 rounded-full px-3.5 text-xs font-medium"
            >
              <Link to="/" target="_self">
                <span>{t('help.backToCrm', 'Voltar ao CRM')}</span>
              </Link>
            </Button>

            <div className="flex items-center gap-2 pl-2 border-l border-border">
              <Avatar size="sm" className="size-7">
                <AvatarFallback className="bg-brand-tint text-brand-fg text-xs font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="hidden md:inline-block text-xs font-medium text-muted-foreground truncate max-w-[120px]">
                {user.name}
              </span>
            </div>
          </div>
        </header>

        {/* Content Viewport */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </div>
      </div>
    </PermissionsProvider>
  );
}
